import { ReactNode, ReactElement, FC } from 'react'

import { clsx } from 'clsx'

import { useCard } from './CardContext.js'

type TitleProps = { size?: CardHeaderSize; children?: ReactNode }

const Title = ({ children, size }: TitleProps) => {
  return (
    <h3
      className={clsx(
        'text-content',
        size === 'sm' && 'text-lg leading-6 font-medium',
      )}
    >
      {children}
    </h3>
  )
}

const Description = ({ children, className }) => {
  return (
    <div className={clsx('text-content-subdued', className)}>
      <p>{children}</p>
    </div>
  )
}

export type CardHeaderSize = 'sm' | 'md'

export type CardHeaderProps = {
  title?: string | ReactElement
  description?: string | ReactElement
  action?: ReactNode
  withBorder?: boolean
  size?: CardHeaderSize
  children?: ReactNode
}

export const CardHeader: FC<CardHeaderProps> = props => {
  const {
    title,
    description,
    action,
    withBorder = false,
    size = 'md',
    children,
  } = props
  const { padding } = useCard()

  let content
  if (!!title && !!description && !!action) {
    content = (
      <div className="-ms-4 -mt-4 flex justify-between items-center flex-wrap sm:flex-nowrap">
        <div className="ms-4 mt-4">
          <Title size={size}>{title}</Title>
          <Description className="mt-1">{description}</Description>
        </div>
        <div className="ms-4 mt-4 flex-shrink-0">{action}</div>
      </div>
    )
  } else if (action) {
    content = (
      <div className="-ms-4 -mt-2 flex items-center justify-between flex-wrap sm:flex-nowrap">
        <div className="ms-4 mt-2">
          <Title size={size}>{title}</Title>
        </div>
        <div className="ms-4 mt-2 flex-shrink-0">{action}</div>
      </div>
    )
  } else if (description) {
    content = (
      <div>
        <Title size={size}>{title}</Title>
        <Description className="mt-1">{description}</Description>
      </div>
    )
  } else if (title) {
    content = <Title size={size}>{title}</Title>
  } else {
    content = children
  }

  return (
    <div
      className={clsx(
        padding === 'md' && 'px-4 py-5 sm:p-6 ',
        padding === 'sm' && 'px-4 py-5 sm:px-3',
        !withBorder && 'pb-0 sm:pb-0',
        withBorder && 'border-b border-line-subdued',
      )}
    >
      {content}
    </div>
  )
}
